import { useMemo } from 'react';

import { TabsProps } from '@/components/chariot-ui';
import { SeedTabValue } from '@/sections/seeds/types';
import { useAuth } from '@/state/auth';

/**
 * Seed counts for different seed types
 * Note: Still used by useSeedData.ts for isAccountEmpty calculation
 */
export interface SeedCounts {
  tld: number;
  domain: number;
  cidr: number;
  ipv4: number;
  ipv6: number;
  webapplication: number;
}

/**
 * Get tab configuration for seeds section
 *
 * @param showDiscovery - Append the Praetorian-only, tenant-wide `discovery`
 *   tab (ENG-5078). Defaults to `false` so the pure function stays a stable
 *   4-tab list for callers/tests that don't care about the Praetorian tab.
 * @returns Tab configuration for Tabs component with static labels (no counts)
 *
 * @example
 * ```tsx
 * const tabOptions = getSeedTabs();
 * const withDiscovery = getSeedTabs({ showDiscovery: true });
 * ```
 */
export const getSeedTabs = ({
  showDiscovery = false,
}: { showDiscovery?: boolean } = {}): TabsProps['tabs'] => {
  const tabs: TabsProps['tabs'] = [
    { value: 'domain', label: 'Domains' },
    { value: 'ip', label: 'CIDR Ranges' },
    { value: 'preseed', label: 'Preseeds' },
    { value: 'webapplication', label: 'Web Applications' },
  ];

  // Discovery is a top-level, tenant-wide, Praetorian-only tab (ENG-5078).
  // Appended (never inserted) so the four seed-type tabs keep their order.
  if (showDiscovery) {
    tabs.push({ value: 'discovery', label: 'Discovery' });
  }

  return tabs;
};

/**
 * Custom hook to get seed tabs configuration with memoization
 *
 * Gates the Praetorian-only `discovery` tab on `useAuth().isPraetorianUser`.
 * During the auth-load window `isPraetorianUser` is briefly `false`, so the
 * tab simply appears once auth resolves — this is the belt-and-suspenders
 * first layer of the ENG-5078 gating (the panel render in `index.tsx` gates
 * on the same signal, and `CustomerDiscoveryTab` self-guards as a third).
 *
 * @returns Memoized tab options
 *
 * @example
 * ```tsx
 * const tabOptions = useSeedTabs();
 * ```
 */
export const useSeedTabs = (): TabsProps['tabs'] => {
  const { isPraetorianUser } = useAuth();
  return useMemo(
    () => getSeedTabs({ showDiscovery: isPraetorianUser }),
    [isPraetorianUser]
  );
};

/**
 * Get the selected tab from tab options
 *
 * @param tabOptions - Available tab options
 * @param activeTab - Current active tab value
 * @returns Selected tab or first tab as fallback
 */
export const getSelectedTab = (
  tabOptions: TabsProps['tabs'],
  activeTab: SeedTabValue
) => {
  return tabOptions.find(tab => tab.value === activeTab) || tabOptions[0];
};
